import { Container } from "@mui/material";
import AppErrorBoundary from "components/Common/AppErrorBoundary";
import {
  useGetAccountDocumentsQuery,
  useGetPartnersQuery,
  useInviteTradePartnersMutation,
  useUpdateStepsMutation,
} from "api/api-accounts";
import { useGetStepsQuery } from "api/api-misc";
import { useGetAccountByIdQuery } from "hooks/useGetAccountById";
import {
  ADD_CREDIT_LIMIT_ALLOCATION,
  ADD_CREDIT_LIMITS,
  ADD_PARTNERS,
  BUYER,
  CL_REQUESTED,
  INCOMLEND_INTERNAL,
  REGISTER_FOR_FINANCING,
  SUPPLIER,
} from "codes";
// Supplier = 3 steps (I am a → Add Partner → Credit Limit Request). Buyer (Reverse Factoring) = 4 steps (add Credit Limit Allocation).
import Loader from "components/Common/Loader";
import SkeletonLoad from "components/Common/SkeletonLoad";
import { mapApiPartnersToFormList } from "components/Company/Lead/utils/reverseFactoringFlow";
import {
  mergePresalesPartnerLists,
  unwrapStepsPayload,
} from "helpers/presalesFormState";
import { shouldReplacePresalesRoute } from "helpers/presalesNavigation";
import CompletionScreen from "components/Company/Registration/CompletionScreen";
import StepperComponent from "components/Stepper";
import { Form, Formik, FormikProps } from "formik";
import {
  RegistrationFormFields,
  defaultValues,
  validationSchemas,
} from "helpers/formFields";
import history from "helpers/history";
import isObjectEmpty from "helpers/isObjectEmpty";
import { resolvePresalesResume } from "helpers/presalesProgress";
import { safeRefetchQuery } from "helpers/safeRefetchQuery";
import { Suspense, useEffect, useRef } from "react";
import { useTranslation } from "react-i18next";
import { Outlet, useLocation } from "react-router-dom";
import { toast } from "react-toastify";
import { useAppDispatch, useAppSelector } from "redux/hooks";
import { setCurrentCompany } from "redux/reducers/companyReducer";
import { setSteps } from "redux/reducers/stepsReducer";
import type { InviteTradePartners, ProcessStep } from "types";

type PresalesRouteProps = {
  currentStep: number;
  maxSteps: number;
  stepLabels: string[];
  labels: Record<number, string>;
  link?: string;
  hasProfile: boolean;
  activeStep: number;
  isSeller: boolean;
};

const PresalesRoute = ({
  currentStep,
  maxSteps,
  stepLabels,
  labels,
  link,
  hasProfile,
  activeStep,
  isSeller,
}: PresalesRouteProps): React.ReactElement => {
  const location = useLocation();
  const lastSyncedPathRef = useRef(location.pathname);

  useEffect(() => {
    if (
      !shouldReplacePresalesRoute({
        currentPath: location.pathname,
        targetPath: link,
        isSeller,
        hasProfile,
        activeStep,
      })
    ) {
      lastSyncedPathRef.current = location.pathname;
      return;
    }
    lastSyncedPathRef.current = link as string;
    history.replace(link as string);
  }, [link, location.pathname, hasProfile, activeStep, isSeller]);

  return (
    <Form>
      <StepperComponent
        step={currentStep}
        labels={stepLabels}
        label={labels[currentStep]}
        maxSteps={maxSteps}
        CompletionScreen={<CompletionScreen />}
      >
        <Container maxWidth="md" sx={{ mt: 5 }}>
          <Outlet />
        </Container>
      </StepperComponent>
    </Form>
  );
};

const Presales = (): React.ReactElement => {
  const { t } = useTranslation();
  const dispatch = useAppDispatch();

  const USER_ROLE = useAppSelector((state) => state.appState.role);
  const CURRENT_COMPANY = useAppSelector(
    (state) => state.companies.currentCompany,
  );
  const stepper = useAppSelector((state) => state.steps.value);
  const companyIdFromSession = stepper?.form?.companyId ?? USER_ROLE?.id ?? "";

  const {
    data: steps,
    refetch: refetchSteps,
    isUninitialized: isStepsUninitialized,
  } = useGetStepsQuery(companyIdFromSession, {
    skip:
      !Boolean(companyIdFromSession) ||
      companyIdFromSession === INCOMLEND_INTERNAL,
  });

  const STEP_FORM_VALUES =
    steps && !isObjectEmpty(steps?.form as object)
      ? steps?.form
      : stepper?.form;

  const resolvedCompanyId = STEP_FORM_VALUES?.companyId ?? companyIdFromSession;

  const { data: company, isFetching: isFetchingCompany } =
    useGetAccountByIdQuery(resolvedCompanyId);
  const accountForResume = company?.id ? company : CURRENT_COMPANY;

  const { data: persistedPartners = [], isFetching: isFetchingPartners } =
    useGetPartnersQuery(resolvedCompanyId ?? "", {
      skip: !Boolean(resolvedCompanyId),
    });

  const persistedStep = steps?.step ?? stepper.step ?? 0;

  const resumeAccount = company?.id
    ? company
    : accountForResume?.id
      ? (accountForResume as typeof company)
      : null;
  const resumeProfile = (
    resumeAccount?.profile ??
    STEP_FORM_VALUES?.companyType ??
    ""
  )
    .trim()
    .toLowerCase();
  const isResumeBuyer =
    resumeProfile === BUYER.toLowerCase() || resumeProfile === "buyer";

  const { data: buyerUploadedDocs = [] } = useGetAccountDocumentsQuery(
    resolvedCompanyId ?? "",
    {
      skip: !Boolean(resolvedCompanyId) || !isResumeBuyer,
    },
  );

  const presalesResume = resolvePresalesResume({
    account: resumeAccount,
    partners: persistedPartners,
    companyType: STEP_FORM_VALUES?.companyType,
    clStatus:
      STEP_FORM_VALUES?.clStatus ?? accountForResume?.credit_limit?.status,
    uploadedDocuments: isResumeBuyer ? buyerUploadedDocs : undefined,
    savedPartnerList: STEP_FORM_VALUES?.list,
    persistedStep,
  });

  const companyType =
    STEP_FORM_VALUES?.companyType || presalesResume.companyType;
  const companyId = resolvedCompanyId;
  const CURRENT_STEP = presalesResume.activeStep;

  const isSeller =
    companyType?.toLowerCase() === SUPPLIER.toLowerCase() ||
    companyType?.toLowerCase() === "seller" ||
    companyType === t("supplier");

  const INITIAL_FORM_VALUES = {
    ...defaultValues,
    ...STEP_FORM_VALUES,
    companyId: resolvedCompanyId ?? STEP_FORM_VALUES?.companyId,
    companyType,
    requested_amount:
      STEP_FORM_VALUES?.requested_amount ??
      accountForResume?.credit_limit?.requested_amount,
    currency:
      STEP_FORM_VALUES?.currency ?? accountForResume?.credit_limit?.currency,
    list: mergePresalesPartnerLists(
      STEP_FORM_VALUES?.list,
      mapApiPartnersToFormList(persistedPartners),
    ),
  };

  const [updateSteps] = useUpdateStepsMutation();

  const [
    inviteTradePartners,
    { isUninitialized, isLoading: isLoadingInvite, isSuccess },
  ] = useInviteTradePartnersMutation();

  // After re-login, update-steps may still say step 0 while account API shows real progress.
  useEffect(() => {
    if (!company?.id || isFetchingCompany) {
      return;
    }

    const savedStep = steps?.step ?? stepper.step ?? 0;
    if (presalesResume.activeStep <= savedStep) {
      return;
    }

    const resumedForm = {
      ...defaultValues,
      ...stepper?.form,
      ...steps?.form,
      companyId: resolvedCompanyId,
      companyType: presalesResume.companyType || companyType,
      clStatus:
        stepper?.form?.clStatus ??
        steps?.form?.clStatus ??
        company.credit_limit?.status ??
        "",
      requested_amount:
        stepper?.form?.requested_amount ??
        company.credit_limit?.requested_amount,
      currency: stepper?.form?.currency ?? company.credit_limit?.currency,
      list: mergePresalesPartnerLists(
        stepper?.form?.list?.length ? stepper.form.list : steps?.form?.list,
        mapApiPartnersToFormList(persistedPartners),
      ),
    };

    dispatch(
      setSteps({
        step: presalesResume.activeStep,
        form: resumedForm,
      }),
    );
  }, [
    company?.id,
    company?.credit_limit?.status,
    dispatch,
    isFetchingCompany,
    persistedPartners.length,
    presalesResume.activeStep,
    presalesResume.companyType,
    resolvedCompanyId,
    stepper.step,
    steps?.step,
  ]);

  const saveAndContinue = async (value: ProcessStep) => {
    try {
      const response = await updateSteps({
        company_id: value.data.companyId,
        step: value.step,
        form: value.data,
      }).unwrap();

      dispatch(
        setSteps(
          unwrapStepsPayload(response, {
            step: value.step,
            form: value.data,
          }),
        ),
      );
      await safeRefetchQuery(refetchSteps, isStepsUninitialized);
      document.getElementById("modal-header")?.scrollIntoView(true);

      company &&
        dispatch(
          setCurrentCompany({
            ...company,
            type: value.data.companyType,
          }),
        );
    } catch (error) {
      const errorFile = (error as { message?: string })?.message;
      toast(`Something went wrong: ${errorFile}`, {
        type: "error",
      });
    }
  };

  // Supplier (main branch): 3 steps — I am a → Add Partner → Credit Limit Request
  // Buyer (Reverse Factoring): 4 steps — I am a → Credit Limit Request → Add Partner → Credit Limit Allocation
  const LABELS_SUPPLIER: Record<string, any> = {
    0: `${t("i-am-a")}`,
    1: `${t("add-partner")}`,
    2: `${t("credit-limit-request")}`,
  };
  const LABELS_BUYER: Record<string, any> = {
    0: `${t("i-am-a")}`,
    1: "Credit Limit Request",
    2: `${t("add-partner")}`,
    3: `${t("credit-limit-allocation")}`,
  };
  const maxSteps = isSeller ? 3 : 4;
  const stepLabels = isSeller
    ? Object.values(LABELS_SUPPLIER)
    : Object.values(LABELS_BUYER);
  const LABELS = isSeller ? LABELS_SUPPLIER : LABELS_BUYER;

  const ROUTING_TABLE: Record<number, Record<number, string>> = isSeller
    ? {
        0: { 3: REGISTER_FOR_FINANCING },
        1: { 0: ADD_PARTNERS },
        2: { 0: ADD_CREDIT_LIMITS },
      }
    : {
        0: { 3: REGISTER_FOR_FINANCING },
        1: { 0: ADD_CREDIT_LIMITS },
        2: { 0: ADD_PARTNERS },
        3: { 0: ADD_CREDIT_LIMIT_ALLOCATION },
      };
  const getPresalesLink = (
    formikProps: FormikProps<RegistrationFormFields>,
  ): string | undefined => {
    const step = CURRENT_STEP;
    const sub_step = step === 0 ? formikProps?.values?.crStage : 0;
    const derivedLink =
      step !== maxSteps ? ROUTING_TABLE[step]?.[sub_step] : undefined;
    if (!presalesResume.hasProfile || presalesResume.activeStep <= 0) {
      return derivedLink;
    }
    return derivedLink ?? presalesResume.activeRoute;
  };

  return (
    <AppErrorBoundary scope="Presales routes">
      <Suspense fallback={<Loader />}>
        {resolvedCompanyId &&
        (isFetchingCompany || isFetchingPartners) &&
        isObjectEmpty(STEP_FORM_VALUES as object) ? (
          <Loader open />
        ) : (
          <Formik
            enableReinitialize
            initialValues={INITIAL_FORM_VALUES}
            validationSchema={
              CURRENT_STEP === 0
                ? (validationSchemas[CURRENT_STEP]?.[
                    Number.isInteger(Number(INITIAL_FORM_VALUES?.crStage))
                      ? Number(INITIAL_FORM_VALUES?.crStage)
                      : 0
                  ] ?? null)
                : (validationSchemas[CURRENT_STEP]?.[0] ?? null)
            }
            onSubmit={(values) => {
              const isBuyerDriven =
                values?.requested_amount &&
                values?.companyType.toLowerCase() === BUYER.toLowerCase();
              const isSupplierDriven =
                values?.companyType?.toLowerCase() === SUPPLIER.toLowerCase() ||
                values?.companyType?.toLowerCase() === "seller";

              // Supplier CL Submit owns the invite. Parent Form submit would
              // either no-op on Yup errors or invite without amount checks.
              if (isSupplierDriven) {
                return;
              }

              // For buyer/reverse_factoring flow, CL is already submitted in step 1
              // Step 2 (Add Partner) should only add partners, not submit CL again
              const isBuyerStep2 = CURRENT_STEP === 2 && isBuyerDriven;
              const clAlreadySubmitted = values?.clStatus === CL_REQUESTED;

              // For buyer step 2, only submit partners (CL already submitted in step 1)
              const requestBody: InviteTradePartners =
                isBuyerStep2 && clAlreadySubmitted
                  ? {
                      company_id: companyId ?? "",
                      data: values?.list ?? [],
                      // Don't include requested_amount for step 2, CL already submitted
                    }
                  : isBuyerDriven
                    ? {
                        company_id: companyId ?? "",
                        data: values?.list ?? [],
                        requested_amount: values.requested_amount,
                        currency: values.currency,
                      }
                    : { company_id: companyId ?? "", data: values?.list ?? [] };

              if (!Boolean(requestBody?.data?.length) || !values) {
                return;
              }

              const afterInviteSuccess = () => {
                saveAndContinue({
                  status: "complete",
                  step: 3,
                  data: {
                    ...values,
                    clStatus: CL_REQUESTED,
                  },
                });
              };

              if (isUninitialized && !isSuccess) {
                inviteTradePartners(requestBody)
                  .unwrap()
                  .then(afterInviteSuccess)
                  .catch((error) => {
                    const errorMessage =
                      typeof (error as { message?: unknown })?.message ===
                      "string"
                        ? (error as { message: string }).message
                        : "";
                    toast(`Something went wrong: ${errorMessage}`, {
                      type: "error",
                    });
                  });
              } else if (!isUninitialized && isSuccess) {
                afterInviteSuccess();
              }
            }}
          >
            {(formikProps) => (
              <>
                {isLoadingInvite && <SkeletonLoad bars={10} />}
                {!isLoadingInvite && (
                  <PresalesRoute
                    currentStep={CURRENT_STEP}
                    maxSteps={maxSteps}
                    stepLabels={stepLabels}
                    labels={LABELS}
                    link={getPresalesLink(formikProps)}
                    hasProfile={presalesResume.hasProfile}
                    activeStep={presalesResume.activeStep}
                    isSeller={isSeller}
                  />
                )}
              </>
            )}
          </Formik>
        )}
      </Suspense>
    </AppErrorBoundary>
  );
};
export default Presales;
